import { useState } from "react";

import { Column, ExpandContent, FormInput, LabelWrapper, Row, Text, ToggleButtons } from "@bridgesplit/ui";
import { OrderedTransactions } from "@bridgesplit/react";
import {
    IS_LOCAL_NX_DEV,
    Result,
    deserializeLegacyTransaction,
    deserializeVersionedTransaction,
    getReadableErrorMessage
} from "@bridgesplit/utils";
import { useActiveWallet } from "@bridgesplit/abf-react";

import { useTransactionSender } from "../transactions";
import { axiosTestingInstance } from "./constants";
import { AppButton } from "../common";

type ParsedJson = string | object;

enum TransactionType {
    Transaction = "Transaction",
    MultipleTransactions = "Transactions Array",
    VersionedTransaction = "Versioned Transaction",
    MultipleVersionedTransactions = "Versioned Transaction Array"
}

const testTxn = JSON.stringify({
    loan: "HtZgJnMXdoTYJMC1P8QbTP1BnusFkxmESyHXNKhMjLJf"
});

export default function TestTransaction() {
    const [url, setUrl] = useState<string | undefined>("http://localhost:8080/test_a_transaction");
    const [inputJson, setInputJson] = useState<string | undefined>(testTxn);
    const [returnType, setReturnType] = useState<TransactionType>(TransactionType.Transaction);
    const { activeWallet } = useActiveWallet();

    const txnSender = useTransactionSender();

    async function getTransactionsWithParams() {
        return await generateTxn({ bodyJSON: inputJson, url, returnType, activeWallet });
    }

    async function send() {
        return await txnSender({ getTransactionsWithParams, description: "Testing...", sendOptions: {} }, {});
    }

    const parsedJson: ParsedJson = (() => {
        try {
            return inputJson ? JSON.parse(inputJson) : null;
        } catch {
            return null;
        }
    })();

    // Function to format JSON with each key-value pair on a new line
    const formatJSON = (json: ParsedJson): string => {
        if (typeof json === "object") {
            return JSON.stringify(json, null, 2);
        } else {
            return json as string;
        }
    };

    const formattedJsonString = formatJSON(parsedJson);

    if (!IS_LOCAL_NX_DEV) {
        return null;
    }

    return (
        <Column spacing={2} sx={{ width: "100%" }}>
            <Row spacing={1}>
                <Text color="caption">For modifications:</Text>
                <code style={{ fontSize: "16px" }}> apps/app/src/app/components/testing/TestTransaction.tsx</code>
            </Row>

            <FormInput
                label="Endpoint URL (Full URL including base)"
                fullWidth
                variant="string"
                value={url}
                setValue={setUrl}
            />
            <FormInput
                label="Body (Raw JSON)"
                fullWidth
                variant="string"
                multiLineHeight={100}
                value={inputJson}
                setValue={setInputJson}
            />
            <LabelWrapper label="Return Type">
                <ToggleButtons
                    value={returnType}
                    setValue={setReturnType}
                    options={Object.values(TransactionType).map((value) => ({ value }))}
                />
            </LabelWrapper>

            <ExpandContent header="Parsed Body JSON">
                {formattedJsonString.split("\n").map((line, index) => (
                    <div key={index}>
                        <code>{line}</code>
                    </div>
                ))}
            </ExpandContent>
            <AppButton
                isTransaction
                asyncCta={{ onClickWithResult: send }}
                variant="contained"
                disabled={!url || !parsedJson}
            >
                Send
            </AppButton>
        </Column>
    );
}

type Params = {
    url: string | undefined;
    bodyJSON: string | undefined;
    returnType: TransactionType;
    activeWallet: string | undefined;
};

async function generateTxn({ bodyJSON, url, returnType, activeWallet }: Params): Promise<Result<OrderedTransactions>> {
    if (!url) return Result.errFromMessage("Missing URL");
    if (!bodyJSON) return Result.errFromMessage("Missing JSON");

    try {
        const res = await axiosTestingInstance.post(url, JSON.parse(bodyJSON), {
            headers: {
                "User-Wallet": activeWallet
            }
        });

        if (!res.data) return Result.errFromMessage("Endpoint failed with empty data");

        const transaction = (() => {
            if (returnType === TransactionType.Transaction) {
                return deserializeLegacyTransaction(res.data);
            }
            if (returnType === TransactionType.VersionedTransaction) {
                return deserializeVersionedTransaction(res.data);
            }

            if (!Array.isArray(res.data)) {
                return null;
            }

            if (returnType === TransactionType.MultipleTransactions) {
                return res.data.map((d) => deserializeLegacyTransaction(d));
            }
            if (returnType === TransactionType.MultipleVersionedTransactions) {
                return res.data.map((d) => deserializeVersionedTransaction(d));
            }
            return null;
        })();

        if (!transaction) return Result.errFromMessage("Unable to create a transaction. Try a different Return Type");

        if (Array.isArray(transaction)) {
            return Result.ok([
                {
                    transactions: transaction.map((t) => ({ identifier: "Test", transaction: t }))
                }
            ]);
        }

        return Result.ok([{ transactions: [{ transaction, identifier: "Test" }] }]);
    } catch (error) {
        return Result.errWithDebug(getReadableErrorMessage("generate transaction"), error);
    }
}
